import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import cookieStorage from './cookie';
import { TUserProfile } from '@/context/global/types/user';
import { fetchUserProfile } from '@/services/userApi';
import { useKaraokeStore } from './karaokeStore';

interface AuthState {
  token: string | null;
  user: TUserProfile | null;
  setAuth: (token: string, user: TUserProfile) => void;
  setUser: (user: TUserProfile) => void;
  initializeUser: () => Promise<void>;
  setAccessToken: (token: string) => void;
  logout: () => void;
  clientType: string | null;
  setClientType: (type: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      setAuth: (token, user) => {
        // Save pending songs before switching queue (guest → user จะ overwrite)
        const { pendingSong, pendingSongs } = useKaraokeStore.getState();

        // Clear pending จาก guest storage ก่อน switch
        if (pendingSong || pendingSongs) {
          useKaraokeStore.getState().setPendingSong(null);
          useKaraokeStore.getState().setPendingSongs(null);
        }

        set({ token, user });
        // Switch to user-specific queue using aliasName as unique identifier
        useKaraokeStore.getState().switchUserQueue(user?.aliasName || null);

        // Restore pending songs ที่บันทึกไว้ตอนเป็น guest
        if (pendingSong) {
          useKaraokeStore.getState().setPendingSong(pendingSong);
        }
        if (pendingSongs) {
          useKaraokeStore.getState().setPendingSongs(pendingSongs);
        }
      },
      setUser: (user) => set({ user }),
      initializeUser: async () => {
        const { token, user } = get();
        if (token && !user) {
          try {
            const profile = await fetchUserProfile(token);
            if (profile) {
              // Save pending songs before switching queue
              const { pendingSong, pendingSongs } = useKaraokeStore.getState();

              // Clear pending จาก guest storage ก่อน switch
              if (pendingSong || pendingSongs) {
                useKaraokeStore.getState().setPendingSong(null);
                useKaraokeStore.getState().setPendingSongs(null);
              }

              set({ user: profile });
              // Switch to user-specific queue after fetching profile
              useKaraokeStore.getState().switchUserQueue(profile?.aliasName || null);

              // Restore pending songs ไปยัง user storage
              if (pendingSong) {
                useKaraokeStore.getState().setPendingSong(pendingSong);
              }
              if (pendingSongs) {
                useKaraokeStore.getState().setPendingSongs(pendingSongs);
              }
            }
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
          }
        }
      },
      setAccessToken: (token) => set({ token }),
      logout: () => {
        set({ token: null, user: null, clientType: null });
        // Switch to guest queue and clear any user-specific data
        useKaraokeStore.getState().switchUserQueue(null);
      },
      clientType: null,
      setClientType: (type: string) => set({ clientType: type }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => cookieStorage),
      partialize: (state) => ({
        token: state.token,
        clientType: state.clientType,
      }),
    },
  ),
);
